import {
  parseMultilineContent,
  parseSingleLineContent,
  stringifyMultilineContent,
  stringifySingleLineContent,
  stringifyVariableAsIdentity,
  stringifyVariableAsPlaceholder,
} from "@repo/app-types/content/plaintext";
import type { MessageTemplateSettingsSectionType } from "@repo/app-types/entities/messageTemplate";
import {
  isInterviewSettingsSectionType,
  type MessageTemplate,
  noVariablesMessageTemplateTypes,
} from "@repo/app-types/entities/messageTemplate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useEffect, useState } from "react";
import { useUpdateMessageTemplateMutation } from "../../../api/entities/messageTemplate.js";
import { MessageTemplateForm } from "./messageTemplateForm.js";

export interface MessageTemplateEditDialogProps {
  messageTemplateSectionType: MessageTemplateSettingsSectionType;
  messageTemplate: MessageTemplate | undefined;
  onClose: () => void;
}

export const MessageTemplateEditDialog: FC<MessageTemplateEditDialogProps> = ({
  messageTemplateSectionType,
  messageTemplate,
  onClose,
}) => {
  const [updateMessageTemplate, { isLoading: isSaving }] =
    useUpdateMessageTemplateMutation();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (messageTemplate) {
      // Some templates do not support variables.
      // In that case use the identity stringifyer so we let all the pieces of text as is.
      const stringifyVariable = noVariablesMessageTemplateTypes.includes(
        messageTemplate.type,
      )
        ? stringifyVariableAsIdentity
        : stringifyVariableAsPlaceholder;

      setSubject(
        stringifySingleLineContent(messageTemplate.subject, {
          stringifyVariable,
        }),
      );
      setBody(
        stringifyMultilineContent(messageTemplate.body, {
          stringifyVariable,
        }),
      );
    }
  }, [messageTemplate]);

  const resetState = () => {
    setSubject("");
    setBody("");
  };

  const onCancel = () => {
    resetState();
    onClose();
  };

  const onEdit = async () => {
    if (!messageTemplate) return;

    // Parse variable only if the template types support them
    const parsesVariables = !noVariablesMessageTemplateTypes.includes(
      messageTemplate.type,
    );

    await updateMessageTemplate({
      id: messageTemplate.id,
      subject: parseSingleLineContent(subject, {
        parsesVariables,
      }),
      body: parseMultilineContent(body, {
        parsesVariables,
      }),
    }).unwrap();

    resetState();
    onClose();
  };

  const isValid = !!subject.trim() && !!body.trim();

  const isInterviewSectionType = isInterviewSettingsSectionType(
    messageTemplateSectionType,
  );

  return (
    <Modal isOpen={!!messageTemplate} onCancel={onCancel} size={Size.LARGE}>
      <ModalHeader onClose={onCancel}>
        {isInterviewSectionType ? "Edit message template" : "Edit template"}
      </ModalHeader>
      <div className="my-4">
        {messageTemplate ? (
          <MessageTemplateForm
            subject={subject}
            body={body}
            type={messageTemplate.type}
            onSubjectChange={setSubject}
            onBodyChange={setBody}
          />
        ) : null}
      </div>
      <div className="flex justify-end space-x-2">
        <Button variant={ButtonVariant.Secondary} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          isLoading={isSaving}
          isDisabled={!isValid}
          onClick={onEdit}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};
